import React, { PropsWithChildren, useContext, useMemo } from 'react';

type Contents = Readonly<Record<string, { value: string; url?: string }>>;

interface AuthorableContentsContextValue {
  contents: Contents;
  getText: (key: string) => string;
  getLink: (key: string) => string;
}

const AuthorableContentsContext =
  React.createContext<AuthorableContentsContextValue>({
    contents: null,
    getText: () => null,
    getLink: () => null,
  });

type AuthorableContentsJourneyDetails = readonly {
  readonly key: string;
  readonly value: string | null;
  readonly url: string | null;
}[];

export interface InterpolationInfo {
  [key: string]: string;
}

interface AuthorableContentsProviderProps {
  value: AuthorableContentsJourneyDetails;
}

export default function AuthorableContentsProvider({
  children,
  value,
}: PropsWithChildren<AuthorableContentsProviderProps>) {
  const contents = useMemo(
    () =>
      value.reduce<Contents>(
        (prev, item) =>
          item
            ? {
                ...prev,
                [item.key]: { value: item.value, url: item.url },
              }
            : prev,
        {},
      ),
    [value],
  );

  const getText = (key: string, interpolationInfo?: InterpolationInfo) => {
    let result = contents?.[key]?.value || '';
    if (interpolationInfo) {
      Object.keys(interpolationInfo).forEach((interpolationKey) => {
        const interpolationValue = interpolationInfo[interpolationKey];
        const regex = new RegExp(`\\{${interpolationKey}\\}`, 'gi');
        result = result.replace(regex, interpolationValue);
      });
    }
    return result;
  };

  const getLink = (key: string) => contents?.[key]?.url || '';

  return (
    <AuthorableContentsContext.Provider value={{ contents, getText, getLink }}>
      {children}
    </AuthorableContentsContext.Provider>
  );
}

export function useAuthorableContents() {
  const context = useContext(AuthorableContentsContext);
  if (!context) {
    throw new Error('useContents should be used inside ContentsContext.');
  }
  return context;
}
